import {
	Component, Input, OnInit, OnChanges, SimpleChanges, SimpleChange,
	ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, HostListener
} from '@angular/core';
import { FilterMetadata, MenuItem } from 'primeng/api';
import { SelectItem } from 'primeng/api';
import { ValueEncoder } from 'DiscoveryEngine.TS/Utils';
import { IpAddress, MacAddress } from 'DiscoveryEngine.TS/Core';
import {
	InfoSetView, InfoSetViewColumn, InfoSetViewEntry, InfoSetViewValue
} from 'UvExplorerServer.Dtos.TS/Session';
//import { Table } from 'primeng/table';
import { UvTable } from 'widget';

@Component({
	selector: 'report-viewer',
	templateUrl: './report-viewer.component.html',
	styleUrls: ['./report-viewer.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportViewerComponent implements OnInit, OnChanges {

	@Input()
	public infoSetView: InfoSetView;

	@Input()
	public updateCallback: (entries: any[]) => void;

	// @ViewChild('dt') dataTable: Table;
	@ViewChild('dt') dataTable: UvTable;

	private pageSize = 50;
	@HostListener('scroll', ['$event'])
	onScroll(event: any) {
		console.log("scrolling");
		const position = Math.round(
			event.target.offsetHeight + event.target.scrollTop
		);
		const bottom = event.target.scrollHeight - 10;
		const atBottom = (position + 50) >= bottom; // Add 50px buffer to bottom
		if (atBottom && this.entries.length > 0 && this.showEntries < this.entries.length) {
			this.showEntries += this.pageSize;
			console.log("showEntries: " + this.showEntries);
		}
	}

	private showEntries = this.pageSize;
	get visibleEntries(): any[] {
		return this.entries.slice(0, this.showEntries);
	}

	get totalEntries(): number {
		return this.entries.length;
	}

	public cols: any[];
	public entries: any[];
	public cmItems: MenuItem[];
	public displaySelectColumnsDialog: boolean;
	public selectItems: SelectItem[];
	public firstPage: number;

	public readonly VirtualScrollItemSize = 29;
	public get TableRowStyle(): string {
		return `height: ${this.VirtualScrollItemSize}px;`;
	}

	constructor(private changeDetector: ChangeDetectorRef) {
		this.infoSetView = null;
		this.updateCallback = null;
		this.cmItems = this.getContextMenuItems();
		this.displaySelectColumnsDialog = false;
		this.resetFirstPage();

		// Force report viewer to update its contents (bug work-around)
		setTimeout(() => {
			this.updateData();
			this.changeDetector.markForCheck();
		}, 0);
	}

	public cellClasses(field: string, rowData: any): string {
		let classes = "";
		if (field === "portStatus" && !!rowData.portStatus) {
			if (rowData.portStatus.toLowerCase() === "open") {
				classes = "port-status-open";
			}
			else {
				classes = "port-status-closed";
			}
		}
		return classes;
	}

	public reloadReport(): void {
		setTimeout(() => { 
			this.entries = [...this.entries];
			this.changeDetector.markForCheck();
		}, 0);
	}

	private resetFirstPage(): void {
		this.firstPage = 0;
	}

	protected getContextMenuItems(): MenuItem[] {
		return [
			{ label: "Select Columns", command: (event) => this.showSelectColumnsDialog() }
		];
	}

	public ngOnInit(): void {
		this.updateData();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		let change = changes["infoSetView"];

		if (change) {
			this.clearFilters(this.dataTable);

			this.updateData();
			this.resetFirstPage();
		}
	}

	private clearFilters(table: UvTable) {
		if (table) {
			for (var key in table.filters) {
				let fieldFilter = table.filters[key];
				if (fieldFilter) {
					if (Array.isArray(fieldFilter)) {
						// TODO:
					}
					else {
						let filterMeta = <FilterMetadata>fieldFilter;
						if (filterMeta) {
							filterMeta.value = '';
						}
					}
				}
			}
		}
	}

	private updateData(): void {
		this.cols = [];
		this.entries = [];
		this.selectItems = [];

		if (this.infoSetView) {

			// Determine which columns contain non-empty values
			let colHasData: boolean[] = new Array(this.infoSetView.columns.length);
			if (this.infoSetView.entries.length > 0) {
				this.infoSetView.entries.forEach((viewEntry: InfoSetViewEntry) => {
					for (let i = 0; i < viewEntry.values.length; ++i) {
						if (viewEntry.values[i].value) {
							colHasData[i] = true;
						}
					}
				}, this);
			}
			else {
				for (let i = 0; i < colHasData.length; ++i) {
					colHasData[i] = true;
				}
			}

			// Initialize columns
			for (let i = 0; i < this.infoSetView.columns.length; ++i) {
				let viewCol = this.infoSetView.columns[i];

				let col = {
					index: i,
					field: viewCol.field,
					header: viewCol.header,
					sortable: "custom",
					sortFunction: (event) => this.doCustomSort(event),
					filterMatchMode: "contains"
				};

				if (viewCol.visible && colHasData[i]) {
					this.cols.push(col);
				}
				this.selectItems.push({ label: col.header, value: col });
			}

			// Initialize entries
			this.infoSetView.entries.forEach((viewEntry: InfoSetViewEntry) => {
				let entry = {};

				for (let i = 0; i < viewEntry.values.length; ++i) {
					let col = this.infoSetView.columns[i];
					let cell = viewEntry.values[i];

					// Initialize value property
					entry[col.field] = cell.value;

					// Initialize tag property
					let tag: any = undefined;

					switch (col.type) {
						case "string":
							tag = cell.tagText;
							break;
						case "number":
							if (cell.value)
								tag = Number.parseFloat(cell.tagText);
							break;
						case "ipaddress":
							if (cell.value)
								tag = IpAddress.Create(cell.tagText);
							break;
						case "macaddress":
							if (cell.value)
								tag = MacAddress.CreateFromString(cell.tagText);
							break;
						default:
							throw new Error("Invalid InfoSetView column type: " + col.type);
					}

					let tagFieldName = col.field + "Tag";
					entry[tagFieldName] = tag;

					// Initialize type property
					let typeFieldName = col.field + "Type";
					entry[typeFieldName] = col.type;
				}

				this.entries.push(entry);
			}, this);

			if (this.updateCallback) {
				this.updateCallback(this.entries);
			}
		}
	}

	private columnExists(colName: string): boolean {
		return this.cols.some((col) => col.field === colName);
	}

	public doCustomSort(event): void {
		if (this.columnExists(event.field)) {
			let _self = this;
			let comparer = function (a: any, b: any): number {

				let tagFieldName = event.field + "Tag";
				let aTag = a[tagFieldName];
				let bTag = b[tagFieldName];

				let typeFieldName = event.field + "Type";
				let type: string = a[typeFieldName];

				let result = 0;
				switch (type) {
					case "string":
						result = _self.compareStrings(aTag, bTag);
						break;
					case "number":
						result = _self.compareNumbers(aTag, bTag);
						break;
					case "ipaddress":
						result = _self.compareIpAddresses(aTag, bTag);
						break;
					case "macaddress":
						result = _self.compareMacAddresses(aTag, bTag);
						break;
					default:
						throw new Error("Invalid InfoSetView column type: " + type);
				}

				return result * event.order;
			}

			this.entries.sort(comparer);
		}
		else {
			//console.error("Sorting on non-existent column");
		}
	}

	private showSelectColumnsDialog(): void {
		this.displaySelectColumnsDialog = true;
	}

	public onColumnSelectionChange(event): void {
		this.cols.sort((a, b) => {
			return a.index - b.index;
		});
	}

	public closeSelectColumnsDialog(): void {
		this.displaySelectColumnsDialog = false;
	}

	public onSelectColumnsDialogHide(): void {
		if (this.cols.length == 0) {
			this.cols.push(this.selectItems[0].value);
			this.changeDetector.detectChanges();
		}
	}

	protected compareStrings(a: string, b: string): number {
		if (a && b)
			return a.localeCompare(b);
		else if (a)
			return 1;
		else if (b)
			return -1;
		else
			return 0;
	}

	protected compareNumbers(a: number, b: number): number {
		if (a !== undefined && b !== undefined)
			return ((a < b) ? -1 : ((a > b) ? 1 : 0));
		else if (a !== undefined)
			return 1;
		else if (b !== undefined)
			return -1;
		else
			return 0;
	}

	protected compareIpAddresses(a: IpAddress, b: IpAddress): number {
		if (a && b)
			return a.CompareTo(b);
		else if (a)
			return 1;
		else if (b)
			return -1;
		else
			return 0;
	}

	protected compareMacAddresses(a: MacAddress, b: MacAddress): number {
		if (a && b)
			return a.CompareTo(b);
		else if (a)
			return 1;
		else if (b)
			return -1;
		else
			return 0;
	}

}
